import React, { useEffect, useState } from 'react';
import { Alert, App, DatePicker, Form, Input, Modal, Radio, Select, Space, Spin, Tag, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { bb, hata, tarih, useHarcirahApi } from './ortak';
import { ULASIM_TANIM } from './giderler';

/**
 * Personelin seyahat planı (26.10.2026 akış: personel planlar → amir onaylar → rezervasyon …) — oluşturma ve plan
 * aşamasında düzenleme (/harcirah-benim POST, PUT /{id}/plan). Amire göndermek kartta ayrı adım. Tip, amir ve il
 * kişinin son seyahatinden ön dolu gelir.
 * 27.10.2026: amir kurala giren personelde KİLİTLİ (şema + şirket; services/harcirah_amir.py) — tarihler seçilince
 * izin denetlenir, amir izinliyse onay bir üst kişiye gider. Rezervasyon talebi (ör. gidiş bir gün önce) rezervasyon
 * personeline, amire ve finansa görünür. Kurala girmeyen personel amiri yalnız listeden seçer: Genel Koordinatör, Genel
 * Müdür, yöneticiler; BT personeline BT ekibi de (/amir → secenekler).
 */
const { RangePicker } = DatePicker;
const { Text } = Typography;
const TALEPLER = [
  'Gidiş bir gün önce olsun', 'Dönüş bir gün sonra olsun', 'Konaklama bir gün önce başlasın', 'Sabah erken uçuş',
  'Akşam dönüş uçuşu', 'Otel iş yerine yakın olsun',
];

export default function PlanPenceresi({ acik, seyahat, onKapat, onKaydedildi }) {
  const api = useHarcirahApi('/harcirah-benim');
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [secenekler, setSecenekler] = useState(null);
  const [onceki, setOnceki] = useState(null);
  const [bekliyor, setBekliyor] = useState(false);
  const [amir, setAmir] = useState(null);  // {kilitli, amir, amir_adi, kural, atlanan, secenekler}
  const duzenleme = !!seyahat;
  const tarihler = Form.useWatch('tarihler', form);
  const ulasim = Form.useWatch('ulasim', form);
  const otel = Form.useWatch('konaklama_gerekli', form);
  const notDegeri = Form.useWatch('rez_notu', form) || '';
  const rezGerekli = ulasim === 'ucak' || otel === true;

  // Amir önizlemesi: kurala giriyorsa kilitli; tarihler seçilince izin denetimi
  useEffect(() => {
    if (!acik) return;
    const p = tarihler?.[0] && tarihler?.[1] ? { bas: tarihler[0].format('YYYY-MM-DD'), bit: tarihler[1].format('YYYY-MM-DD') } : {};
    api.get('/amir', p).then(setAmir).catch(() => setAmir(null));
  }, [acik, tarihler?.[0]?.valueOf(), tarihler?.[1]?.valueOf()]); // eslint-disable-line react-hooks/exhaustive-deps
  // Önceki seyahatten / eski plandan gelen amir artık listede değilse yeniden seçilsin
  useEffect(() => {
    if (!amir || amir.kilitli) return;
    const secili = form.getFieldValue('amir');
    if (secili && !(amir.secenekler || []).some((x) => x.kullanici_no === secili)) form.setFieldsValue({ amir: undefined });
  }, [amir, onceki]); // eslint-disable-line react-hooks/exhaustive-deps
  const amirSecenekleri = ['Yönetim', 'BT ekibi'].map((g) => ({
    label: bb(g),
    options: (amir?.secenekler || []).filter((x) => x.grup === g)
      .map((x) => ({ value: x.kullanici_no, label: `${x.ad_soyad} — ${x.pozisyon}` })),
  })).filter((g) => g.options.length);
  const talepSec = (t) => {
    const l = notDegeri.split(';').map((x) => x.trim()).filter(Boolean);
    form.setFieldsValue({ rez_notu: (l.includes(t) ? l.filter((x) => x !== t) : [...l, t]).join('; ') });
  };

  useEffect(() => {
    if (acik && !secenekler) api.get('/secenekler').then(setSecenekler).catch((e) => message.error(hata(e)));
  }, [acik]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!acik) return;
    form.resetFields();
    setOnceki(null);
    if (seyahat) {
      form.setFieldsValue({
        seyahat_turu: seyahat.seyahat_turu || undefined, il_no: seyahat.il_no, aciklama: seyahat.aciklama,
        tarihler: [dayjs(seyahat.baslangic), dayjs(seyahat.bitis)], tip: seyahat.tip, amir: seyahat.amir,
        ulasim: seyahat.ulasim || undefined, konaklama_gerekli: seyahat.konaklama_gerekli ?? undefined,
        rez_notu: seyahat.rez_notu || '',
      });
    } else {
      api.get('/onceki').then((o) => {
        setOnceki(o);
        if (o) form.setFieldsValue({ tip: o.tip, amir: o.amir, il_no: o.il_no });  // amir kilitliyse gönderilmez
      }).catch(() => {});
    }
  }, [acik, seyahat]); // eslint-disable-line react-hooks/exhaustive-deps

  const kaydet = () => form.validateFields().then((v) => {
    const govde = {
      seyahat_turu: v.seyahat_turu, il_no: v.il_no, aciklama: v.aciklama || '', tip: v.tip,
      baslangic: v.tarihler[0].format('YYYY-MM-DD'), bitis: v.tarihler[1].format('YYYY-MM-DD'),
      ulasim: v.ulasim, konaklama_gerekli: v.konaklama_gerekli, rez_notu: v.rez_notu || '',
      ...(amir?.kilitli ? {} : { amir: v.amir }),
    };
    setBekliyor(true);
    return (duzenleme ? api.put(`/${seyahat.id}/plan`, govde) : api.post('', govde))
      .then((s) => { message.success(duzenleme ? 'Plan güncellendi.' : 'Plan oluşturuldu; amire göndermeyi unutmayın.'); onKaydedildi(s); })
      .catch((e) => message.error(hata(e)))
      .finally(() => setBekliyor(false));
  }).catch(() => {});

  return (
    <Modal rootClassName="hr-modal" open={acik} onCancel={onKapat} onOk={kaydet} okText="Kaydet" cancelText="Vazgeç"
      confirmLoading={bekliyor} width={720} destroyOnHidden title={duzenleme ? 'Planı Düzenle' : 'Seyahat Planla'}>
      <Spin spinning={!secenekler}>
        {!duzenleme && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={onceki
              ? `Tip, amir ve il son seyahatinizden (${tarih(onceki.baslangic)}–${tarih(onceki.bitis)}) dolduruldu.`
              : 'Planı kaydedip amirinize gönderin. Amir onaylayınca otel / uçak gerekiyorsa rezervasyon personeli ayarlar.'} />
        )}
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item name="seyahat_turu" label="Seyahat Türü" rules={[{ required: true, message: 'Seyahat türünü seçin' }]}>
            <Radio.Group optionType="button" buttonStyle="solid" options={secenekler?.seyahat_turleri || []}
              style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Form.Item name="il_no" label="Gidilen İl" style={{ flex: '1 1 200px' }} rules={[{ required: true, message: 'İl seçin' }]}>
              <Select showSearch optionFilterProp="label"
                options={(secenekler?.iller || []).map((i) => ({ value: i.il_no, label: i.il_ad }))} />
            </Form.Item>
            <Form.Item name="aciklama" label="Açıklama" style={{ flex: '2 1 300px' }}>
              <Input maxLength={250} placeholder="Yer / iş (ör. enerji lokasyonları)" />
            </Form.Item>
          </div>
          <Form.Item name="tarihler" label="Başlangıç – Bitiş" rules={[{ required: true, message: 'Tarih seçin' }]}>
            <RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Form.Item name="ulasim" label="Ulaşım" style={{ flex: '1 1 300px' }} rules={[{ required: true, message: 'Ulaşım şeklini seçin' }]}>
              <Radio.Group optionType="button" buttonStyle="solid"
                options={Object.entries(ULASIM_TANIM).map(([k, u]) => ({ value: k, label: <span>{u.ikon} {u.ad}</span> }))} />
            </Form.Item>
            <Form.Item name="konaklama_gerekli" label="Otel gerekiyor mu?" style={{ flex: '1 1 200px' }}
              rules={[{ required: true, message: 'Seçin' }]}>
              <Radio.Group optionType="button" buttonStyle="solid" options={[{ value: true, label: 'Evet' }, { value: false, label: 'Hayır' }]} />
            </Form.Item>
          </div>
          {rezGerekli && (
            <Form.Item label="Rezervasyon Talebi / Not" extra="Rezervasyon personeli, amir ve finans görür. Ör. bir gün önce gitmek, geç dönüş, otel tercihi.">
              <Space size={[4, 4]} wrap style={{ marginBottom: 6 }}>
                {TALEPLER.map((t) => (
                  <Tag.CheckableTag key={t} checked={notDegeri.split(';').map((x) => x.trim()).includes(t)} onChange={() => talepSec(t)}
                    style={{ border: '1px solid rgba(128,128,128,0.35)' }}>{t}</Tag.CheckableTag>
                ))}
              </Space>
              <Form.Item name="rez_notu" noStyle>
                <Input.TextArea rows={2} maxLength={500} showCount placeholder="İsteğe bağlı: seçin ya da kendiniz yazın (ör. 14.11 akşam uçuşu)" />
              </Form.Item>
            </Form.Item>
          )}
          <Form.Item name="tip" label="Harcırah Tipi" rules={[{ required: true, message: 'Tip seçin' }]}
            extra={duzenleme ? 'Tip değişirse tüm günlerin öğün hakları yeni tipe göre yeniden açılır.' : 'Kaydedince seyahat ayrıntısı açılır; gün gün öğün hakkını orada açıp kapatabilirsiniz.'}>
            <Radio.Group optionType="button" buttonStyle="solid" options={secenekler?.tipler || []} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} />
          </Form.Item>
          {amir?.kilitli ? (
            <Form.Item label="Amir (Planı Onaylayacak Kişi)" required>
              <div style={{ border: '1px solid rgba(128,128,128,0.3)', borderRadius: 8, padding: '8px 12px' }}>
                <Space size={6}><LockOutlined /><Text strong>{amir.amir_adi}</Text></Space>
                <div><Text type="secondary" style={{ fontSize: 12 }}>Kuraldan: {amir.kural}</Text></div>
              </div>
              {amir.atlanan?.length > 0 && (
                <Alert type="warning" showIcon style={{ marginTop: 8 }}
                  message={amir.atlanan.map((a) => `${a.ad} bu tarihlerde izinli (${a.izin})`).join('; ')}
                  description={`Onay bir üst kişiye gider: ${amir.amir_adi}.`} />
              )}
            </Form.Item>
          ) : (
            <Form.Item name="amir" label="Amir (Planı Onaylayacak Kişi)" rules={[{ required: true, message: 'Amir seçin' }]}
              extra={amir ? 'Pozisyonunuz amir kurallarında tanımlı değil. Genel Koordinatör, Genel Müdür ya da bir yönetici seçin'
                + ((amir.secenekler || []).some((x) => x.grup === 'BT ekibi') ? ' (BT ekibinden biri de olabilir).' : '.') : null}>
              <Select showSearch optionFilterProp="label" loading={!amir} options={amirSecenekleri} placeholder="Amir Seçin" />
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Modal>
  );
}
